import type { BarkKind } from '../../../sim/types';
import { IDENTITY, lerp, put } from './batch';
import type { EventOf, Fx } from './context';
import type { Drawer } from './particles';

/*
 * Bark markers: a small self-lit glyph over the head of a soldier who calls out, so his
 * state reads without sound. An "!" for the barks that mean trouble for the ranger, a
 * diamond for the ones that mean he is unsure or pinned. The other barks show nothing.
 */

const MARKER_LIFE = 0.8;
/** Base of the glyph above the ground: clear of a helmet at character scale. */
const MARKER_HEIGHT = 2.5;

const ALERT: ReadonlySet<BarkKind> = new Set<BarkKind>(['contact', 'alarm', 'grenade']);
const QUERY: ReadonlySet<BarkKind> = new Set<BarkKind>(['searching', 'suppressed', 'help']);

/** Uses no randomness, so markers never disturb the shared stream the other effects draw on. */
export function bark(fx: Fx, e: EventOf<'bark'>): void {
  const batch = ALERT.has(e.kind) ? fx.set.b.barkAlert : QUERY.has(e.kind) ? fx.set.b.barkQuery : null;
  if (!batch) return;
  // One glyph per head: a new bark replaces whatever that soldier was still showing.
  fx.particles.retire('marker', e.id);
  const p = fx.particles.spawn('marker', batch, e.x, fx.groundY(e.x, e.z) + MARKER_HEIGHT, e.z, 1, MARKER_LIFE);
  p.ref = e.id;
}

export function markerDrawer(fx: Fx): Drawer {
  return (p, camera) => {
    // Ride along with the soldier who barked, for as long as the world still has him.
    const enemies = fx.world?.enemies;
    if (enemies) {
      for (let i = 0; i < enemies.length; i++) {
        const enemy = enemies[i];
        if (enemy && enemy.id === p.ref) {
          p.x = lerp(enemy.prevX, enemy.x, fx.alpha);
          p.z = lerp(enemy.prevZ, enemy.z, fx.alpha);
          p.y = fx.groundY(p.x, p.z) + MARKER_HEIGHT;
          break;
        }
      }
    }
    // Three hand-drawn frames: pops in oversize for a beat, holds, drops out small.
    const t = p.age / p.life;
    const s = t < 0.12 ? 1.35 : t > 0.85 ? 0.6 : 1;
    put(p.batch, camera, p.x, p.y, p.z, s, s, s, IDENTITY);
  };
}
